import React, { useState, useEffect } from 'react';
import { 
  Plus,
  CheckCircle,
  Circle,
  Clock,
  Calendar,
  User,
  Building2,
  Edit,
  Trash2,
  ExternalLink,
  X,
  UserCircle,
  Eye,
  Trash
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import EmpresaDetailModal from './EmpresaDetailModal';
import EmpresaSearchSelect from './EmpresaSearchSelect';
import * as api from '../services/api';

// Helper function to convert 24h time to 12h AM/PM format
const formatTimeAMPM = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Helper function to get today's date in Puerto Rico timezone (YYYY-MM-DD format)
const getTodayPR = () => {
  const now = new Date();
  const prDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Puerto_Rico' }));
  const year = prDate.getFullYear();
  const month = String(prDate.getMonth() + 1).padStart(2, '0');
  const day = String(prDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TareasPage = ({ currentUser, canDelete = () => true, globalSearch = '' }) => {
  const [tasks, setTasks] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('Pendiente'); // Default to Pendiente
  const [userFilter, setUserFilter] = useState('mine'); // Default to current user's tasks
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmpresaModal, setShowEmpresaModal] = useState(false);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedEmpresaContacts, setSelectedEmpresaContacts] = useState([]);
  const [editEmpresaContacts, setEditEmpresaContacts] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: '',
    due_time: '',
    priority: 'Media',
    empresa_id: '',
    contact_id: '',
    assigned_to: ''
  });

  // Task completion states
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [completionNote, setCompletionNote] = useState('');
  const [completionChecks, setCompletionChecks] = useState({
    llamadaSinRespuesta: false,
    emailEnviado: false,
    mensajeTexto: false,
    whatsAppEnviado: false,
    visitado: false,
    reunionCompletada: false,
    noShow: false,
    esperandoRespuesta: false
  });
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [followUpTask, setFollowUpTask] = useState({
    title: '',
    date: '',
    time: '09:00',
    priority: 'Alta'
  });

  const isAdmin = currentUser?.role === 'administrator' || currentUser?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksRes, empresasRes, usersRes] = await Promise.all([
        api.getTasks(),
        api.getEmpresas(),
        api.getUsers()
      ]);
      setTasks(tasksRes.data);
      setEmpresas(empresasRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format date for search
  const formatDateForSearch = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      // Return multiple formats for matching
      return [
        date.toLocaleDateString('es-PR', { month: 'long', day: 'numeric', year: 'numeric' }),
        date.toLocaleDateString('es-PR', { month: 'short', day: 'numeric' }),
        date.toLocaleDateString('es-PR'),
        dateStr
      ].join(' ');
    } catch {
      return dateStr;
    }
  };

  const filteredTasks = tasks
    .filter(task => {
      // Global search filter
      if (globalSearch) {
        const searchLower = globalSearch.toLowerCase();
        const matchesSearch = 
          task.title?.toLowerCase().includes(searchLower) ||
          task.description?.toLowerCase().includes(searchLower) ||
          task.empresa_name?.toLowerCase().includes(searchLower) ||
          task.contact_name?.toLowerCase().includes(searchLower) ||
          task.assigned_to?.toLowerCase().includes(searchLower) ||
          task.due_date?.includes(searchLower) ||
          (task.due_date && formatDateForSearch(task.due_date).toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }
      // User filter
      if (userFilter === 'mine') {
        if (task.assigned_to !== currentUser?.name) return false;
      } else if (userFilter !== 'all') {
        if (task.assigned_to !== userFilter) return false;
      }
      // Status filter (no more 'all' option)
      return task.status === statusFilter;
    })
    .sort((a, b) => {
      // Sort by due_date and due_time
      // Tasks without date go to the end
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      
      // Compare dates
      const dateA = new Date(a.due_date + 'T' + (a.due_time || '00:00'));
      const dateB = new Date(b.due_date + 'T' + (b.due_time || '00:00'));
      
      return dateA - dateB; // Ascending order (earliest first)
    });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Alta': return 'bg-red-100 text-red-700';
      case 'Media': return 'bg-yellow-100 text-yellow-700';
      case 'Baja': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completada': return <CheckCircle size={20} className="text-green-500" />;
      case 'Pendiente': return <Circle size={20} className="text-gray-400" />;
      default: return <Circle size={20} className="text-gray-400" />;
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title) {
      alert('El título es requerido');
      return;
    }
    if (!newTask.assigned_to) {
      alert('Debe seleccionar un usuario');
      return;
    }
    try {
      const taskData = {
        ...newTask,
        status: 'Pendiente',
        empresa_id: newTask.empresa_id && newTask.empresa_id !== '' ? newTask.empresa_id : null,
        contact_id: newTask.contact_id && newTask.contact_id !== '' ? newTask.contact_id : null
      };
      const response = await api.createTask(taskData, currentUser?.email || 'system');
      setTasks([...tasks, response.data]);
      setShowAddModal(false);
      setNewTask({ title: '', description: '', due_date: '', due_time: '', priority: 'Media', empresa_id: '', contact_id: '', assigned_to: '' });
      setSelectedEmpresaContacts([]);
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Error al crear tarea: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEmpresaChange = (empresaId) => {
    setNewTask({ ...newTask, empresa_id: empresaId === "none" ? "" : empresaId, contact_id: '' });
    if (empresaId && empresaId !== "none") {
      const empresa = empresas.find(e => e.id === empresaId);
      setSelectedEmpresaContacts(empresa?.contacts || []);
    } else {
      setSelectedEmpresaContacts([]);
    }
  };

  const handleEditEmpresaChange = (empresaId) => {
    setSelectedTask({ ...selectedTask, empresa_id: empresaId === "none" ? "" : empresaId, contact_id: '' });
    if (empresaId && empresaId !== "none") {
      const empresa = empresas.find(e => e.id === empresaId);
      setEditEmpresaContacts(empresa?.contacts || []);
    } else {
      setEditEmpresaContacts([]);
    }
  };

  const handleUpdateTask = async () => {
    try {
      const response = await api.updateTask(selectedTask.id, selectedTask);
      setTasks(tasks.map(t => t.id === selectedTask.id ? response.data : t));
      setShowEditModal(false);
      setSelectedTask(null);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleToggleStatus = async (task) => {
    // If task is pending, open completion modal
    if (task.status === 'Pendiente') {
      setCompletingTask(task);
      setCompletionNote('');
      setCompletionChecks({
        llamadaSinRespuesta: false,
        emailEnviado: false,
        mensajeTexto: false,
        whatsAppEnviado: false,
        visitado: false,
        reunionCompletada: false,
        noShow: false,
        esperandoRespuesta: false
      });
      setShowFollowUpForm(false);
      setFollowUpTask({ title: `Seguimiento: ${task.title}`, date: '', time: '09:00', priority: 'Alta' });
      setShowCompletionModal(true);
    } else if (task.status === 'Completada') {
      // Toggle back to pending
      try {
        const response = await api.updateTask(task.id, { status: 'Pendiente' });
        setTasks(tasks.map(t => t.id === task.id ? response.data : t));
      } catch (error) {
        console.error('Error updating task status:', error);
      }
    }
  };

  const handleCompleteTask = async (createFollowUp = false) => {
    if (!completingTask) return;
    
    try {
      // Build note content with checkmarks
      let noteContent = '';
      const checkLabels = {
        llamadaSinRespuesta: '📞 Llamada sin Respuesta',
        emailEnviado: '📧 Email Enviado',
        mensajeTexto: '💬 Mensaje de Texto Enviado',
        whatsAppEnviado: '📱 WhatsApp Enviado',
        visitado: '🏢 Visitado',
        reunionCompletada: '🤝 Reunión Completada',
        noShow: '❌ No Show',
        esperandoRespuesta: '⏳ Esperando Respuesta'
      };
      
      const selectedChecks = Object.entries(completionChecks)
        .filter(([_, value]) => value)
        .map(([key, _]) => checkLabels[key]);
      
      if (selectedChecks.length > 0) {
        noteContent += `Resultado: ${selectedChecks.join(', ')}\n\n`;
      }
      
      if (completionNote.trim()) {
        noteContent += completionNote;
      }
      
      // Mark task as completed
      await api.updateTask(completingTask.id, { status: 'Completada' });
      
      // Create note if there's content
      if (noteContent.trim() && completingTask.empresa_id) {
        const fullNoteContent = `[Resultado de tarea "${completingTask.title}"]\n${noteContent}`;
        await api.addNote(completingTask.empresa_id, { content: fullNoteContent }, currentUser?.name || 'Sistema');
      }
      
      // Create follow-up task if requested
      if (createFollowUp && followUpTask.title && followUpTask.date) {
        const newFollowUpTask = {
          title: followUpTask.title,
          description: `Seguimiento de la tarea completada.\n\nNota anterior:\n${noteContent}`,
          due_date: followUpTask.date,
          due_time: followUpTask.time,
          priority: followUpTask.priority,
          status: 'Pendiente',
          empresa_id: completingTask.empresa_id,
          contact_id: completingTask.contact_id,
          assigned_to: completingTask.assigned_to
        };
        await api.createTask(newFollowUpTask, currentUser?.email || 'system');
      }
      
      // Refresh tasks
      await loadData();
      
      // Close modal
      setShowCompletionModal(false);
      setCompletingTask(null);
      setCompletionNote('');
      setShowFollowUpForm(false);
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Error al completar tarea: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('¿Está seguro de eliminar esta tarea?')) {
      try {
        await api.deleteTask(taskId);
        setTasks(tasks.filter(t => t.id !== taskId));
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const handleDeleteCompletedTasks = async () => {
    const completedCount = tasks.filter(t => t.status === 'Completada').length;
    if (completedCount === 0) {
      alert('No hay tareas completadas para eliminar');
      return;
    }
    
    if (window.confirm(`¿Está seguro de eliminar ${completedCount} tareas completadas? Esta acción no se puede deshacer.`)) {
      try {
        const response = await api.deleteCompletedTasks();
        const deletedCount = response.data.deleted_count;
        setTasks(tasks.filter(t => t.status !== 'Completada'));
        alert(`Se eliminaron ${deletedCount} tareas completadas`);
      } catch (error) {
        console.error('Error deleting completed tasks:', error);
        alert('Error al eliminar tareas completadas: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleAddToGoogleCalendar = (task) => {
    const startDate = task.due_date ? task.due_date.replace(/-/g, '') : '';
    const startTime = task.due_time ? task.due_time.replace(/:/g, '') : '090000';
    const endTime = task.due_time ? 
      String(parseInt(task.due_time.split(':')[0]) + 1).padStart(2, '0') + task.due_time.split(':')[1].replace(/:/g, '') + '00' 
      : '100000';
    
    const empresa = empresas.find(e => e.id === task.empresa_id);
    const description = `${task.description || ''}\n\nEmpresa: ${empresa?.name || 'N/A'}\nPrioridad: ${task.priority}`;
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(task.title)}&dates=${startDate}T${startTime}/${startDate}T${endTime}&details=${encodeURIComponent(description)}&sf=true&output=xml`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  const openEditModal = (task) => {
    setSelectedTask({ ...task });
    if (task.empresa_id) {
      const empresa = empresas.find(e => e.id === task.empresa_id);
      setEditEmpresaContacts(empresa?.contacts || []);
    } else {
      setEditEmpresaContacts([]);
    }
    setShowEditModal(true);
  };

  const getEmpresaName = (empresaId) => {
    const empresa = empresas.find(e => e.id === empresaId);
    return empresa?.name || '';
  };

  const getContactName = (contactId) => {
    for (const empresa of empresas) {
      const contact = empresa.contacts?.find(c => c.id === contactId);
      if (contact) return contact.name;
    }
    return '';
  };

  const pendingCount = tasks.filter(t => t.status === 'Pendiente').length;
  const completedCount = tasks.filter(t => t.status === 'Completada').length;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A843]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Tareas</h1>
        <div className="flex items-center gap-2">
          {completedCount > 0 && canDelete('tasks') && (
            <Button 
              onClick={handleDeleteCompletedTasks}
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="delete-completed-tasks-btn"
            >
              <Trash size={18} className="mr-2" />
              Eliminar Completadas ({completedCount})
            </Button>
          )}
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-[#D4A843] hover:bg-[#c49a3a] text-white"
          >
            <Plus size={18} className="mr-2" />
            Nueva Tarea
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('Pendiente')}>
          <CardContent className="p-4 flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Circle size={20} className="text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pendientes</p>
              <p className="text-xl font-bold text-gray-800">{pendingCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('Completada')}>
          <CardContent className="p-4 flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={20} className="text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Completadas</p>
              <p className="text-xl font-bold text-gray-800">{completedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap items-center gap-3">
        {/* User Filter */}
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="w-44">
            <User size={16} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mine">Mis tareas</SelectItem>
            {isAdmin && <SelectItem value="all">Todas</SelectItem>}
            {isAdmin && users.map(user => (
              <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Filters */}
        <div className="flex items-center space-x-2">
          <Button 
            variant={statusFilter === 'Pendiente' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('Pendiente')}
            className={statusFilter === 'Pendiente' ? 'bg-[#5a5a5a]' : ''}
          >
            Pendientes
          </Button>
          <Button 
            variant={statusFilter === 'Completada' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('Completada')}
            className={statusFilter === 'Completada' ? 'bg-[#5a5a5a]' : ''}
          >
            Completadas
          </Button>
        </div>
      </div>

      {/* Tasks List - Grouped by Day */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-200">
            {(() => {
              // Group tasks by date
              const groupedTasks = {};
              filteredTasks.forEach(task => {
                const dateKey = task.due_date || 'Sin fecha';
                if (!groupedTasks[dateKey]) {
                  groupedTasks[dateKey] = [];
                }
                groupedTasks[dateKey].push(task);
              });
              
              // Sort date keys
              const sortedDates = Object.keys(groupedTasks).sort((a, b) => {
                if (a === 'Sin fecha') return 1;
                if (b === 'Sin fecha') return -1;
                return new Date(a) - new Date(b);
              });
              
              return sortedDates.map(dateKey => {
                const tasksForDate = groupedTasks[dateKey];
                const todayPR = getTodayPR();
                const isToday = dateKey !== 'Sin fecha' && dateKey === todayPR;
                const isPast = dateKey !== 'Sin fecha' && dateKey < todayPR;
                
                // Format date for display
                let displayDate = dateKey;
                if (dateKey !== 'Sin fecha') {
                  // Parse the date parts directly from YYYY-MM-DD to avoid timezone issues
                  const [year, month, day] = dateKey.split('-').map(Number);
                  const dateObj = new Date(year, month - 1, day, 12, 0, 0);
                  const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
                  displayDate = dateObj.toLocaleDateString('es-ES', options);
                  displayDate = displayDate.charAt(0).toUpperCase() + displayDate.slice(1);
                }
                
                return (
                  <div key={dateKey}>
                    {/* Date Header */}
                    <div className={`px-4 py-2 sticky top-0 z-10 ${
                      isToday ? 'bg-[#D4A843] text-white' : 
                      isPast ? 'bg-red-100 text-red-800' : 
                      'bg-gray-100 text-gray-700'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">
                          {isToday ? '📅 HOY - ' : isPast ? '⚠️ ' : '📆 '}
                          {displayDate}
                        </span>
                        <Badge variant="outline" className={isToday ? 'border-white text-white' : ''}>
                          {tasksForDate.length} {tasksForDate.length === 1 ? 'tarea' : 'tareas'}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Tasks for this date */}
                    <div className="divide-y divide-gray-100">
                      {tasksForDate
                        .sort((a, b) => {
                          const timeA = a.due_time || '23:59';
                          const timeB = b.due_time || '23:59';
                          return timeA.localeCompare(timeB);
                        })
                        .map((task) => (
                        <div 
                          key={task.id} 
                          className={`p-4 flex items-start space-x-4 hover:bg-gray-50 transition-colors ${
                            task.status === 'Completada' ? 'opacity-60' : ''
                          }`}
                        >
                          <button 
                            onClick={() => handleToggleStatus(task)}
                            className="flex-shrink-0 mt-1"
                          >
                            {getStatusIcon(task.status)}
                          </button>
                          
                          {/* Time Badge - More prominent */}
                          <div className="flex-shrink-0 w-20 text-center">
                            {task.due_time ? (
                              <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-lg">
                                <Clock size={12} className="inline mr-1" />
                                <span className="text-sm font-bold">{formatTimeAMPM(task.due_time)}</span>
                              </div>
                            ) : (
                              <div className="bg-gray-100 text-gray-500 px-2 py-1 rounded-lg text-xs">
                                Sin hora
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-gray-800 ${
                              task.status === 'Completada' ? 'line-through text-gray-500' : ''
                            }`}>
                              {task.title}
                            </p>
                            {task.description && (
                              <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-3 mt-2">
                              <span className="text-xs text-gray-500 flex items-center">
                                <User size={12} className="mr-1" />
                                {task.assigned_to}
                              </span>
                              {task.empresa_id && (
                                <button 
                                  onClick={(e) => { e.stopPropagation(); setSelectedEmpresaId(task.empresa_id); setShowEmpresaModal(true); }}
                                  className="text-xs text-[#D4A843] hover:underline flex items-center"
                                >
                                  <Building2 size={12} className="mr-1" />
                                  {getEmpresaName(task.empresa_id)}
                                  <Eye size={10} className="ml-1" />
                                </button>
                              )}
                              {task.contact_id && (
                                <span className="text-xs text-gray-500 flex items-center">
                                  <UserCircle size={12} className="mr-1" />
                                  {getContactName(task.contact_id)}
                                </span>
                              )}
                            </div>
                          </div>
                          <Badge className={getPriorityColor(task.priority)}>
                            {task.priority}
                          </Badge>
                          <div className="flex items-center space-x-1">
                            <button 
                              onClick={() => handleAddToGoogleCalendar(task)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Añadir a Google Calendar"
                            >
                              <ExternalLink size={16} className="text-blue-500" />
                            </button>
                            <button 
                              onClick={() => openEditModal(task)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <Edit size={16} className="text-gray-500" />
                            </button>
                            {canDelete('tasks') && (
                              <button 
                                onClick={() => handleDeleteTask(task.id)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                data-testid={`delete-task-${task.id}`}
                              >
                                <Trash2 size={16} className="text-red-500" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
            {filteredTasks.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                No se encontraron tareas
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Task Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Nueva Tarea</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Título de la tarea"
                data-testid="task-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4A843] text-sm"
                rows={2}
                placeholder="Descripción de la tarea"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Empresa (Opcional)</label>
              <EmpresaSearchSelect
                empresas={empresas}
                value={newTask.empresa_id || null}
                onChange={(val) => handleEmpresaChange(val || 'none')}
                placeholder="Buscar empresa..."
                showNone={true}
                noneLabel="Sin empresa"
                data-testid="task-empresa-search"
              />
            </div>
            {selectedEmpresaContacts.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contacto (Opcional)</label>
                <Select 
                  value={newTask.contact_id || "none"} 
                  onValueChange={(value) => setNewTask({ ...newTask, contact_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="task-contact-select">
                    <SelectValue placeholder="Seleccionar contacto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin contacto</SelectItem>
                    {selectedEmpresaContacts.map(contact => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} {contact.position && `- ${contact.position}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                <Input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                <Input
                  type="time"
                  value={newTask.due_time}
                  onChange={(e) => setNewTask({ ...newTask, due_time: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
              <Select 
                value={newTask.priority} 
                onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Alta">Alta</SelectItem>
                  <SelectItem value="Media">Media</SelectItem>
                  <SelectItem value="Baja">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asignado a</label>
              <Select 
                value={newTask.assigned_to} 
                onValueChange={(value) => setNewTask({ ...newTask, assigned_to: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar usuario" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancelar</Button>
            <Button onClick={handleAddTask} className="bg-[#D4A843] hover:bg-[#c49a3a]" data-testid="save-task-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Editar Tarea</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4 py-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <Input
                  value={selectedTask.title}
                  onChange={(e) => setSelectedTask({ ...selectedTask, title: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={selectedTask.description || ''}
                  onChange={(e) => setSelectedTask({ ...selectedTask, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4A843] text-sm"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empresa (Opcional)</label>
                <EmpresaSearchSelect
                  empresas={empresas}
                  value={selectedTask.empresa_id || null}
                  onChange={(val) => handleEditEmpresaChange(val || 'none')}
                  placeholder="Buscar empresa..."
                  showNone={true}
                  noneLabel="Sin empresa"
                  data-testid="edit-task-empresa-search"
                />
              </div>
              {editEmpresaContacts.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contacto (Opcional)</label>
                  <Select 
                    value={selectedTask.contact_id || "none"} 
                    onValueChange={(value) => setSelectedTask({ ...selectedTask, contact_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar contacto" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin contacto</SelectItem>
                      {editEmpresaContacts.map(contact => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.name} {contact.position && `- ${contact.position}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                  <Input
                    type="date"
                    value={selectedTask.due_date || ''}
                    onChange={(e) => setSelectedTask({ ...selectedTask, due_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                  <Input
                    type="time"
                    value={selectedTask.due_time || ''}
                    onChange={(e) => setSelectedTask({ ...selectedTask, due_time: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
                  <Select 
                    value={selectedTask.priority} 
                    onValueChange={(value) => setSelectedTask({ ...selectedTask, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Alta">Alta</SelectItem>
                      <SelectItem value="Media">Media</SelectItem>
                      <SelectItem value="Baja">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <Select 
                    value={selectedTask.status} 
                    onValueChange={(value) => setSelectedTask({ ...selectedTask, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pendiente">Pendiente</SelectItem>
                      <SelectItem value="Completada">Completada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asignado a</label>
                <Select 
                  value={selectedTask.assigned_to} 
                  onValueChange={(value) => setSelectedTask({ ...selectedTask, assigned_to: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
            <Button 
              onClick={() => handleAddToGoogleCalendar(selectedTask)} 
              variant="outline"
              className="text-blue-500 border-blue-500"
            >
              <ExternalLink size={16} className="mr-1" /> Google Calendar
            </Button>
            <Button onClick={handleUpdateTask} className="bg-[#D4A843] hover:bg-[#c49a3a]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Empresa Detail Modal */}
      <EmpresaDetailModal
        open={showEmpresaModal}
        onOpenChange={setShowEmpresaModal}
        empresaId={selectedEmpresaId}
        currentUser={currentUser}
        onEmpresaUpdate={() => loadData()}
        onEmpresaDelete={() => { setShowEmpresaModal(false); loadData(); }}
      />

      {/* Task Completion Modal */}
      <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Completar Tarea</DialogTitle>
          </DialogHeader>
          {completingTask && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-sm">{completingTask.title}</p>
                {completingTask.description && (
                  <p className="text-xs text-gray-500 mt-1">{completingTask.description}</p>
                )}
              </div>

              {/* Result Checkboxes - More compact */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resultado de la gestión:</label>
                <div className="grid grid-cols-2 gap-1">
                  <label className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                    <input
                      type="checkbox"
                      checked={completionChecks.llamadaSinRespuesta}
                      onChange={(e) => setCompletionChecks({ ...completionChecks, llamadaSinRespuesta: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300"
                    />
                    <span>📞 Llamada sin Respuesta</span>
                  </label>
                  <label className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                    <input
                      type="checkbox"
                      checked={completionChecks.emailEnviado}
                      onChange={(e) => setCompletionChecks({ ...completionChecks, emailEnviado: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300"
                    />
                    <span>📧 Email Enviado</span>
                  </label>
                  <label className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                    <input
                      type="checkbox"
                      checked={completionChecks.mensajeTexto}
                      onChange={(e) => setCompletionChecks({ ...completionChecks, mensajeTexto: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300"
                    />
                    <span>💬 Mensaje de Texto</span>
                  </label>
                  <label className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                    <input
                      type="checkbox"
                      checked={completionChecks.whatsAppEnviado}
                      onChange={(e) => setCompletionChecks({ ...completionChecks, whatsAppEnviado: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300"
                    />
                    <span>📱 WhatsApp Enviado</span>
                  </label>
                  <label className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                    <input
                      type="checkbox"
                      checked={completionChecks.visitado}
                      onChange={(e) => setCompletionChecks({ ...completionChecks, visitado: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300"
                    />
                    <span>🏢 Visitado</span>
                  </label>
                  <label className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                    <input
                      type="checkbox"
                      checked={completionChecks.reunionCompletada}
                      onChange={(e) => setCompletionChecks({ ...completionChecks, reunionCompletada: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300"
                    />
                    <span>🤝 Reunión Completada</span>
                  </label>
                  <label className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                    <input
                      type="checkbox"
                      checked={completionChecks.noShow}
                      onChange={(e) => setCompletionChecks({ ...completionChecks, noShow: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300"
                    />
                    <span>❌ No Show</span>
                  </label>
                  <label className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                    <input
                      type="checkbox"
                      checked={completionChecks.esperandoRespuesta}
                      onChange={(e) => setCompletionChecks({ ...completionChecks, esperandoRespuesta: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300"
                    />
                    <span>⏳ Esperando Respuesta</span>
                  </label>
                </div>
              </div>

              {/* Note - More compact */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nota adicional:</label>
                <textarea
                  value={completionNote}
                  onChange={(e) => setCompletionNote(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4A843] min-h-[60px]"
                  placeholder="Escribe una nota sobre el resultado de la tarea..."
                />
              </div>

              {/* Follow-up toggle */}
              <div className="border-t pt-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showFollowUpForm}
                    onChange={(e) => setShowFollowUpForm(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">Crear tarea de seguimiento</span>
                </label>
              </div>

              {/* Follow-up form - Compact */}
              {showFollowUpForm && (
                <div className="p-3 bg-blue-50 rounded-lg space-y-2">
                  <p className="text-xs font-medium text-blue-800">Nueva tarea de seguimiento:</p>
                  <Input
                    value={followUpTask.title}
                    onChange={(e) => setFollowUpTask({ ...followUpTask, title: e.target.value })}
                    placeholder="Título"
                    className="h-8 text-sm"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      type="date"
                      value={followUpTask.date}
                      onChange={(e) => setFollowUpTask({ ...followUpTask, date: e.target.value })}
                      className="h-8 text-sm"
                    />
                    <Input
                      type="time"
                      value={followUpTask.time}
                      onChange={(e) => setFollowUpTask({ ...followUpTask, time: e.target.value })}
                      className="h-8 text-sm"
                    />
                    <Select 
                      value={followUpTask.priority} 
                      onValueChange={(value) => setFollowUpTask({ ...followUpTask, priority: value })}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Alta">Alta</SelectItem>
                        <SelectItem value="Media">Media</SelectItem>
                        <SelectItem value="Baja">Baja</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowCompletionModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => handleCompleteTask(false)} 
              className="bg-green-600 hover:bg-green-700"
              size="sm"
            >
              <CheckCircle size={14} className="mr-1" />
              Completar
            </Button>
            {showFollowUpForm && followUpTask.date && (
              <Button 
                onClick={() => handleCompleteTask(true)} 
                className="bg-[#D4A843] hover:bg-[#c49a3a]"
                size="sm"
              >
                <CheckCircle size={14} className="mr-1" />
                + Seguimiento
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TareasPage;
